import {
    Component,
    ComponentInterface,
    Element,
    h,
    Host,
    Prop,
    State,
} from "@stencil/core";
import dayjs from "dayjs";
import {
    FilterBlockTypesEnum,
    FilterData,
    FilterDataProvider,
    FilterDataProviderState,
    FilterItem,
    getFilterDataProvider,
} from "../../apis/stateful-content";
import {
    getTranslateFn,
    TranslateFn,
    Translations,
    getTranslationSource,
    TranslationRenderType,
} from "../stateful-content/stateful-content-utils";

@Component({
    tag: "ux-iws-stateful-filter-tags",
    styleUrl: "stateful-filter-tags.scss",
})
export class StatefulFilterTags implements ComponentInterface {
    private baseClass = "ux-iws-stateful-filter-tags";
    private filterProvider: FilterDataProvider;
    private translate: TranslateFn;
    private translationRenderType: TranslationRenderType =
        TranslationRenderType.Filter;

    @Element() domNode: HTMLUxIwsStatefulFilterTagsElement;

    /** print debug info */
    @Prop() debug: boolean = false;
    /** scope used to retrieve the filter data provider */
    @Prop() scope: string = "";
    /** @deprecated JSON string or object with translations */
    @Prop() translations: Translations | string = "";
    /** optional language for translations (mainly used for storybook) - otherwise language is automatically detected by ux.i18n */
    @Prop() language?: string = "";

    @State() activeFilterItems: FilterItem[] = [];

    private _log = (...args: any[]) => {
        if (this.debug && console && typeof console.log === "function") {
            console.log(`[Stateful-Filter-Tags]:`, ...args);
        }
    };

    componentWillLoad() {
        this.filterProvider = getFilterDataProvider(this.scope);
        this.filterProvider.subscribe(this.handleFilterProviderState);
        this.filterProvider.getData();
        this.setTranslations();
    }

    private getStatefulFilter(): HTMLUxIwsStatefulFilterElement {
        return document.querySelector(
            `ux-iws-stateful-filter[scope="${this.scope}"]`
        );
    }

    /**
     * handle script tag translations as well as deprecated translations prop fallback
     **/
    private setTranslations = () => {
        const translationsObject = this.translations.length
            ? this.translations
            : getTranslationSource(
                  this.scope,
                  this.domNode,
                  this.translationRenderType
              );
        this.translate = getTranslateFn(translationsObject);
    };

    private handleFilterProviderState = ({
        filterData,
        filterQuery,
    }: FilterDataProviderState) => {
        if (filterData === null) {
            return;
        }
        this._log(`handleFilterProviderState filterQuery: "${filterQuery}"`);
        this.activeFilterItems = this.getActiveFilterItemsTranslated(
            filterData,
            filterQuery
        );
    };

    private getActiveFilterItemsTranslated = (
        filterData: FilterData,
        filterQuery
    ): FilterItem[] => {
        const { translate } = this;
        const items: FilterItem[] = [];
        const statefulFilter = this.getStatefulFilter();

        if (
            statefulFilter.datePicker &&
            (statefulFilter.rangeStartValue !== undefined ||
                statefulFilter.rangeEndValue !== undefined)
        ) {
            const queryStrings = filterQuery.split("&");
            let startDateValue;
            let endDateValue;

            const { rangeStartName, rangeEndName } = statefulFilter;

            for (const param of queryStrings) {
                const [key, value] = param.split("=");
                if (key === rangeStartName) {
                    startDateValue = value;
                } else if (key === rangeEndName) {
                    endDateValue = value;
                }
            }

            const dateFormat = statefulFilter.dateFormat;
            const formattedStartDate =
                startDateValue !== undefined
                    ? dayjs(startDateValue).format(dateFormat)
                    : "∞";
            const formattedEndDate =
                endDateValue !== undefined
                    ? dayjs(endDateValue).format(dateFormat)
                    : "∞";
            const datePickerValue = `${
                formattedStartDate === "Invalid Date" ? "∞" : formattedStartDate
            } - ${
                formattedEndDate === "Invalid Date" ? "∞" : formattedEndDate
            }`;

            const dateRangeItem = {
                value: `${startDateValue}&${endDateValue}`,
                name: "date-range",
                label: datePickerValue,
            };

            if (startDateValue !== undefined || endDateValue !== undefined) {
                items.push(dateRangeItem);
            }
        }

        filterData.filterBlocks.forEach((filterBlock) => {
            switch (filterBlock.type) {
                case FilterBlockTypesEnum.Checkbox:
                    filterBlock.filterItems.forEach((item) => {
                        if (!item.active) {
                            return;
                        }
                        const translationKey = `${item.name}.label`;
                        const name = item.label || item.value;
                        const translated = translate(translationKey);
                        const label =
                            translated !== translationKey ? translated : name;
                        items.push({
                            ...item,
                            label,
                        });
                    });

                    break;
                case FilterBlockTypesEnum.Dropdown:
                    filterBlock.filterItems.forEach((item) => {
                        if (!item.active) {
                            return;
                        }
                        const translationKey = `${filterBlock.name}.values.${item.value}`;
                        const name = item.label || item.name || item.value;
                        const translated = translate(translationKey);
                        const label =
                            translated !== translationKey ? translated : name;
                        items.push({
                            ...item,
                            label,
                        });
                    });
                    break;
                case FilterBlockTypesEnum.Select:
                    filterBlock.options.forEach((option) => {
                        if (!option.selected) {
                            return;
                        }
                        const translationKey = `${filterBlock.name}.values.${option.value}`;
                        const name = option.label || option.value;
                        const translated = translate(translationKey);
                        const label =
                            translated !== translationKey ? translated : name;
                        items.push({
                            name: filterBlock.name,
                            value: option.value,
                            active: option.selected,
                            label,
                        });
                    });
                    break;
                default:
                    break;
            }
        });
        return items;
    };

    private renderFilterTag = (item: FilterItem) => {
        return (
            <ux-tag
                key={`${item.name}-${item.value}`}
                removable
                onClick={() => this.removeActiveFilter(item)}
                data-testid={`remove-filter-${item.name}-${item.value}`}
            >
                {item.label}
            </ux-tag>
        );
    };

    private removeActiveFilter = (filterToRemove: FilterItem) => {
        this.activeFilterItems = this.activeFilterItems.filter(
            (filter) => filter !== filterToRemove
        );
        // special case for date range
        if (filterToRemove.name === "date-range") {
            const { rangeStartName, rangeEndName } =
                this.getStatefulFilter() || {};
            this.removeActiveFilter({
                ...filterToRemove,
                name: rangeStartName,
                value: filterToRemove.value.split("&")[0],
            });
            this.removeActiveFilter({
                ...filterToRemove,
                name: rangeEndName,
                value: filterToRemove.value.split("&")[1],
            });
            return;
        }
        const params = new URLSearchParams(this.filterProvider.filterQuery);
        for (const key of params.keys()) {
            const values = params.getAll(key);
            if (values.includes(filterToRemove.value)) {
                const index = values.indexOf(filterToRemove.value);
                if (index > -1) {
                    values.splice(index, 1);
                }
                params.delete(key);
                values.forEach((value) => params.append(key, value));
                break;
            }
        }

        this.filterProvider.setFilterQuery(params.toString());
    };

    render() {
        const { activeFilterItems } = this;
        return (
            <Host class={this.baseClass}>
                {this.filterProvider?.filterData &&
                    activeFilterItems.length > 0 && (
                        <div class={`${this.baseClass}__container`}>
                            <div class="ux-tag-list">
                                {activeFilterItems.map((item) =>
                                    this.renderFilterTag(item)
                                )}
                                {activeFilterItems.length > 0 && (
                                    <ux-iws-stateful-filter-remove-button
                                        variant="tag"
                                        scope={this.scope}
                                    ></ux-iws-stateful-filter-remove-button>
                                )}
                            </div>
                        </div>
                    )}
                <slot name="translations" />
            </Host>
        );
    }
}
